/**
 * Authenticated API Service
 *
 * Provides a wrapper around fetch() that automatically includes
 * Firebase authentication tokens in all API requests.
 *
 * Usage:
 *   import { api } from '../services/api';
 *
 *   const data = await api.post('/generate-patent-sketches', {
 *     patent_id: '123',
 *     specification: 'text...'
 *   });
 */

import { auth } from '../config/firebase.js';

const API_BASE_URL = '/api';
const DEFAULT_TIMEOUT = 60000; // 60 seconds

/** Fired on window when the API refuses a request for want of a plan. See GlobalUpgradePrompt. */
export const UPGRADE_REQUIRED_EVENT = 'autoinvent:upgrade-required';

class ApiService {
  constructor() {
    this.consecutiveErrors = 0;
    this.lastErrorTime = null;
  }

  /**
   * Parse API responses safely.
   * Falls back to plain text when response body is not valid JSON.
   * @param {Response} response
   * @returns {Promise<any>}
   */
  async parseResponse(response) {
    const contentType = response.headers.get('content-type') || '';
    const rawBody = await response.text();

    if (!rawBody) return null;

    const expectsJson = contentType.includes('application/json') || contentType.includes('+json');

    if (expectsJson) {
      try {
        return JSON.parse(rawBody);
      } catch (error) {
        console.warn('Invalid JSON response received from API:', error);
      }
    }

    try {
      return JSON.parse(rawBody);
    } catch {
      return rawBody;
    }
  }

  /**
   * Get the current user's authentication token
   * @returns {Promise<string>} Firebase ID token
   */
  async getAuthToken() {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    return await user.getIdToken();
  }

  /**
   * Create a timeout promise that rejects after specified milliseconds
   * @param {number} ms - Timeout in milliseconds
   * @returns {Promise} Promise that rejects on timeout
   */
  createTimeout(ms) {
    return new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), ms)
    );
  }

  /**
   * Make an authenticated API request with timeout
   * @param {string} endpoint - API endpoint (e.g., '/generate-patent-sketches')
   * @param {Object} options - Fetch options
   * @returns {Promise<any>} Response data
   */
  async request(endpoint, options = {}) {
    const timeout = options.timeout || DEFAULT_TIMEOUT;

    try {
      // Get auth token
      const token = await this.getAuthToken();

      // Prepare headers
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      };

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        // Make request with timeout
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          ...options,
          headers,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Parse response safely (JSON when possible, text fallback otherwise)
        const data = await this.parseResponse(response);

        // Handle errors
        if (!response.ok) {
          const structuredMessage =
            data && typeof data === 'object'
              ? (data.message || data.error)
              : null;

          const textMessage =
            typeof data === 'string' && data.trim()
              ? data.trim()
              : null;

          const fallbackMessage = `API request failed (${response.status} ${response.statusText || 'Unknown Error'})`;
          const error = new Error(structuredMessage || textMessage || fallbackMessage);
          error.status = response.status;
          error.data = data;

          /*
           * 402 means the server refused a write for want of a subscription.
           *
           * Handled once here rather than at each call site: any of a hundred callers could be
           * the one that trips it, and a caller that forgets shows a raw "Payment Required"
           * instead of the offer. It opens the upgrade prompt over the current page —
           * GlobalUpgradePrompt listens for this event — rather than navigating to /paywall:
           * bouncing somebody off the page they were using, to a screen that sold only
           * subscriptions, is how the first tap of every free session used to end.
           *
           * With the web on a free tier the API only sends this when HARD_PAYWALL is switched
           * back on; the per-feature limits answer with their own 403 codes, handled where
           * they happen.
           */
          if (response.status === 402 && data?.code === 'PAYMENT_REQUIRED') {
            window.dispatchEvent(new CustomEvent(UPGRADE_REQUIRED_EVENT));
          }

          throw error;
        }

        // Reset error counter on success
        this.consecutiveErrors = 0;
        this.lastErrorTime = null;

        return data ?? {};
      } catch (fetchError) {
        clearTimeout(timeoutId);

        // Handle abort (timeout)
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timed out. The server is taking too long to respond.');
        }

        throw fetchError;
      }
    } catch (error) {
      // Track consecutive errors
      this.consecutiveErrors++;
      this.lastErrorTime = Date.now();

      // If multiple consecutive errors or timeout, suggest reload
      if (this.consecutiveErrors >= 3 || error.message.includes('timed out')) {
        this.handleCriticalError(error);
      }

      // Re-throw authentication errors
      if (error.message === 'User not authenticated') {
        console.error('User is not authenticated. Please log in.');
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        console.error('Network error: Unable to connect to server');
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }

      // Log and re-throw other errors
      console.error('API request error:', error);
      throw error;
    }
  }

  /**
   * Handle critical errors that may require page reload
   * @param {Error} error - The error that occurred
   */
  handleCriticalError(error) {
    console.error('Critical error detected:', error);

    // Check if user has been experiencing errors for a while
    if (this.lastErrorTime && Date.now() - this.lastErrorTime > 30000) {
      // More than 30 seconds of errors
      const shouldReload = window.confirm(
        'The application appears to be unresponsive. Would you like to reload the page?'
      );

      if (shouldReload) {
        window.location.reload();
      }
    }
  }

  /**
   * Make a GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Additional fetch options
   * @returns {Promise<any>} Response data
   */
  async get(endpoint, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'GET',
    });
  }

  /**
   * Make a POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Additional fetch options
   * @returns {Promise<any>} Response data
   */
  async post(endpoint, body = {}, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Make a PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Additional fetch options
   * @returns {Promise<any>} Response data
   */
  async put(endpoint, body = {}, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  /**
   * Make a PATCH request — a partial update, where PUT would mean replacing the whole thing.
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Additional fetch options
   * @returns {Promise<any>} Response data
   */
  async patch(endpoint, body = {}, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  /**
   * Make a DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Additional fetch options
   * @returns {Promise<any>} Response data
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }

  /**
   * Upload a file with authentication
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - Form data with file
   * @returns {Promise<any>} Response data
   */
  async uploadFile(endpoint, formData) {
    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type for FormData - browser will set it with boundary
        },
        body: formData,
      });

      const data = await this.parseResponse(response);

      if (!response.ok) {
        const structuredMessage =
          data && typeof data === 'object'
            ? (data.message || data.error)
            : null;

        const textMessage =
          typeof data === 'string' && data.trim()
            ? data.trim()
            : null;

        const error = new Error(structuredMessage || textMessage || 'File upload failed');
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return data ?? {};
    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    }
  }

  /**
   * Get user's monthly discovery feed
   * @returns {Promise<Object>} Feed data with patents
   */
  async getDiscoveryFeed() {
    return this.get('/discovery/feed', { timeout: 180000 }); // 3 min — feed creation can be slow
  }

  /**
   * Load more patents from the discovery feed
   * @param {number} offset - Current offset in the feed
   * @returns {Promise<Object>} More patents
   */
  async getMoreDiscoveryPatents(offset) {
    return this.get(`/discovery/feed/more?offset=${offset}`);
  }

  async searchPatents(query, limit = 30) {
    return this.get(`/discovery/search?q=${encodeURIComponent(query)}&limit=${limit}`, { timeout: 90000 });
  }

  /**
   * Get current discovery quota status
   * @returns {Promise<Object>} Quota info
   */
  async getDiscoveryQuota() {
    return this.get('/discovery/quota');
  }

  // ===== Explorer wall (rebuilt Abandoned Patent Explorer) =====
  // Only mounted server-side when EXPLORER_WALL_ENABLED=true; these 404 otherwise.
  // See docs/explorer-rebuild-plan.md.

  /**
   * Today's 16 tiles plus header and progress state.
   * @returns {Promise<Object>} { success, data: { dayKey, wallSize, patents,
   *   corpusCount, poolSize, personalizedCount, opens } }
   */
  async getWall() {
    return this.get('/wall');
  }

  /**
   * Spend one of today's opens. Reopening a patent already opened today is free.
   * Rejects with a 403 carrying code OPEN_LIMIT_REACHED when the allowance is gone.
   * @param {string} patentId
   */
  async openWallTile(patentId) {
    return this.post('/wall/open', { patentId });
  }

  /**
   * Replace the industries the wall is drawn from. Takes effect on the NEXT wall; today's set
   * stays as it is. Rejects unknown keys — see utils/wallInterests.js.
   * @param {string[]} industries
   */
  async updateWallIndustries(industries) {
    return this.put('/wall/industries', { industries });
  }

  /**
   * Just the opens counter, without refetching the wall.
   */
  async getWallOpens() {
    return this.get('/wall/opens');
  }

  /**
   * Search the corpus. Free plans get one query a day and a capped result slice; a spent
   * search returns 200 with `spent: true` rather than an error, so the caller can keep the
   * previous results on screen.
   * @param {string} q
   * @returns {Promise<Object>} { success, data: { query, spent, results, totalFound, capped, searches } }
   */
  async searchWall(q) {
    return this.get(`/wall/search?q=${encodeURIComponent(q)}`);
  }

  /**
   * Everything the full-record page needs. Does not consume an open.
   * @param {string} patentId
   */
  async getWallPatent(patentId) {
    return this.get(`/wall/patent/${encodeURIComponent(patentId)}`);
  }

  /**
   * Generated detail fields for an opened patent (plain summary, buildability, status).
   * Fetched separately from the open itself so the modal can appear immediately — the
   * first request for a given patent waits on generation, every one after is cached.
   * Does not consume an open.
   * @param {string} patentId
   */
  async getWallEnrichment(patentId) {
    return this.get(`/wall/enrichment/${encodeURIComponent(patentId)}`);
  }

  /**
   * Take a 48-hour hold. Replaces any existing hold — confirm with the user first.
   * Rejects 409 with code ALREADY_HELD when someone else claimed it.
   * @param {string} patentId
   */
  async holdWallPatent(patentId) {
    return this.post('/wall/hold', { patentId });
  }

  /** Release the current hold. Idempotent. */
  async releaseWallHold() {
    return this.delete('/wall/hold');
  }

  /**
   * Mark a patent "not for me". Never rationed, and it does NOT remove the tile from
   * today's wall — it only shapes future days. See docs/explorer-rebuild-plan.md, D18.
   * @param {string} patentId
   */
  async dismissWallTile(patentId, undo = false) {
    return this.post('/wall/dismiss', { patentId, undo });
  }

  // ===== Reinvent flow =====
  // Four steps ending in the existing PPA intake. A run is consumed on START, so an
  // in-progress run is resumable rather than costing a free user their only reinvention.

  /** Remaining allowance, for gating the entry points. */
  async getReinventState() {
    return this.get('/reinvent/state');
  }

  /** Start (or resume) a run. Returns the run with its claim-cited directions. */
  async startReinvent(patentId) {
    return this.post('/reinvent/start', { patentId });
  }

  async getReinventRun(runId) {
    return this.get(`/reinvent/${encodeURIComponent(runId)}`);
  }

  /** Record chosen directions, get the three questions scoped to them. */
  async chooseReinventDirections(runId, directionIds) {
    return this.post(`/reinvent/${encodeURIComponent(runId)}/directions`, { directionIds });
  }

  /** Turn the user's three answers into the three the filing form asks for. */
  async submitReinventAnswers(runId, answers) {
    return this.post(`/reinvent/${encodeURIComponent(runId)}/answers`, answers);
  }

  async finishReinvent(runId) {
    return this.post(`/reinvent/${encodeURIComponent(runId)}/finish`, {});
  }

  // ===== Saved (bookmarked) abandoned patents =====

  /**
   * Get all patents the user has saved
   * @returns {Promise<Object>} { success, data: [...patents], count }
   */
  async getSavedPatents() {
    return this.get('/saved-patents');
  }

  /**
   * Save (bookmark) an abandoned patent
   * @param {Object} patent - Full patent snapshot (must include patentId and title)
   * @returns {Promise<Object>}
   */
  async savePatent(patent) {
    return this.post('/saved-patents', { patent });
  }

  /**
   * Remove a saved patent
   * @param {string} patentId
   * @returns {Promise<Object>}
   */
  async unsavePatent(patentId) {
    return this.delete(`/saved-patents/${encodeURIComponent(patentId)}`);
  }

  /**
   * Refresh discovery session
   * @returns {Promise<Object>} New session ID
   */
  async refreshDiscoverySession() {
    return this.post('/discovery/session');
  }

  // ===== Competitive-intelligence monitoring (Premium/'business' only) =====

  /** Entitlement probe — { hasMonitoring, plan }. Never 403s. */
  async getMonitoringAccess() {
    return this.get('/monitoring/access');
  }

  /** List the user's monitors (auto-creates a default one if none exist). */
  async getMonitors() {
    return this.get('/monitoring/monitors', { timeout: 90000 });
  }

  /**
   * Create a monitor from free text, or from a patent/idea via { sourceRef }.
   *
   * The longest request in the app, and the only slow one that was left on the sixty-second
   * default: it embeds the description, derives the field it sits in, and matches the whole
   * corpus against it before returning, because work started after a response is not
   * guaranteed to run on a serverless host. Twenty seconds is typical and it can run past a
   * minute, at which point the client gave up on a request the server went on to finish.
   */
  async createMonitor(body) {
    return this.post('/monitoring/monitors', body, { timeout: 180000 });
  }

  /** Update mutable monitor fields (label, enabledSources, thresholds, digestCadence, slack). */
  async updateMonitor(id, body) {
    return this.request(`/monitoring/monitors/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async deleteMonitor(id) {
    return this.delete(`/monitoring/monitors/${encodeURIComponent(id)}`);
  }

  /** Stop watching a competitor. Idempotent. */
  async removeMonitorEntity(monitorId, entityId) {
    return this.delete(
      `/monitoring/monitors/${encodeURIComponent(monitorId)}/entities/${encodeURIComponent(entityId)}`,
    );
  }

  /** Add a competitor to watch, by name. */
  /**
   * "That is not them." Sends the right website for a watched company; the server either moves
   * the watch to the company that site belongs to, or corrects this one's domain.
   */
  async correctMonitorEntity(monitorId, entityId, website) {
    return this.patch(
      `/monitoring/monitors/${encodeURIComponent(monitorId)}/entities/${encodeURIComponent(entityId)}`,
      { website },
    );
  }

  /** `website` is only sent on a retry, after the server said it could not find the company. */
  /**
   * Watch a company on an agent.
   *
   * Slow for the same reason creating an agent is: the company is resolved, its site checked,
   * its pages discovered and its last year pulled, all before the response, because work
   * started after a response is not guaranteed to run on a serverless host. Measured at 10s,
   * 15s and 45s against real companies, so the sixty-second default was a coin flip.
   */
  async addMonitorEntity(id, name, website) {
    return this.post(
      `/monitoring/monitors/${encodeURIComponent(id)}/entities`,
      { name, ...(website ? { website } : {}) },
      { timeout: 180000 },
    );
  }

  /** The feed: hits joined with signals. Optional label filter. */
  async getMonitorFeed(id, label) {
    const q = label ? `?label=${encodeURIComponent(label)}` : '';
    return this.get(`/monitoring/monitors/${encodeURIComponent(id)}/feed${q}`, { timeout: 90000 });
  }

  /** Flip read state / leave feedback on a hit. */
  async updateMonitorHit(monitorId, signalId, patch) {
    return this.request(
      `/monitoring/monitors/${encodeURIComponent(monitorId)}/hits/${encodeURIComponent(signalId)}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    );
  }

  /**
   * "Email me a link" from the desktop wall.
   *
   * Sent to the address on the account, never one the client names -- see
   * controllers/handoff.controller.ts. The path is where they were turned away from, so the
   * link puts them back on the screen they were reaching for rather than a generic landing.
   */
  async sendDesktopLink(path) {
    return this.post('/handoff-email', { path });
  }

  /** Attach a creator's referral code to the just-created account. */
  async attributeReferral(code) {
    return this.post('/referral/attribute', { code });
  }

  /** Whether a typed referral code is real, so signup can confirm it. */
  /**
   * Confirm a referral code names a real creator.
   *
   * A bare fetch, deliberately not this.get(). request() calls getAuthToken() first, which
   * THROWS when nobody is signed in -- and the only place this runs is the signup form,
   * where by definition nobody is. It threw straight into the caller's silent catch, so the
   * "Referred by ..." confirmation could never appear for anyone. The endpoint itself is
   * public (routes.ts mounts it without authenticateUser); it was only the client wrapper
   * that demanded a token.
   */
  async checkReferralCode(code) {
    const res = await fetch(`${API_BASE_URL}/referral/check?code=${encodeURIComponent(code)}`);
    if (!res.ok) throw new Error(`Referral check failed: ${res.status}`);
    return res.json();
  }

  /** The free field preview behind the paywall (screens 01-03). Not Premium-gated. */
  async runMonitoringPreview(seedText, websiteUrl) {
    return this.post('/monitoring/preview', { seedText, websiteUrl }, { timeout: 90000 });
  }

  /** A preview this user has already run, if any. */
  async getMonitoringPreview() {
    return this.get('/monitoring/preview');
  }

  /** Setup step 1 — the filed patent to offer, if any. */
  async getSetupStartingPoint() {
    return this.get('/monitoring/setup/starting-point');
  }

  /** Draft a description from the user's own site. Returns a draft, not a seed. */
  async describeFromWebsite(websiteUrl) {
    return this.post('/monitoring/setup/describe', { websiteUrl }, { timeout: 45000 });
  }

  /** Setup step 3 — competitors already visible in the described field. */
  /**
   * Find a patent filed elsewhere, by number or by name.
   *
   * The server decides which it got. A number comes back as `patent`; a title or an applicant
   * name comes back as `results` to choose from. Both are absent when nothing matched, which
   * is an outcome rather than an error.
   */
  async lookupPatent(query) {
    return this.get(`/monitoring/setup/patent?q=${encodeURIComponent(query)}`, { timeout: 30000 });
  }

  /** Pull the full record for a search result the user picked, by its application number. */
  async lookupPatentByApplication(applicationNumber) {
    return this.get(
      `/monitoring/setup/patent?application=${encodeURIComponent(applicationNumber)}`,
      { timeout: 30000 },
    );
  }

  async getSetupSuggestions(seedText) {
    return this.post('/monitoring/setup/suggestions', { seedText });
  }

  /** Typeahead over companies we already know. */
  async searchEntities(q) {
    return this.get(`/monitoring/entities/search?q=${encodeURIComponent(q)}`);
  }

  /**
   * The expanded read on one alert. Written server-side on first open and stored, so this is a
   * model call once and a cheap read every time after.
   */
  /** A follow-up question on one alert. Not stored — asked once, read once. */
  async askAboutHit(monitorId, signalId, question, history) {
    return this.post(
      `/monitoring/monitors/${encodeURIComponent(monitorId)}/hits/${encodeURIComponent(signalId)}/ask`,
      { question, history },
      { timeout: 60000 },
    );
  }

  async getHitBriefing(monitorId, signalId) {
    return this.get(
      `/monitoring/monitors/${encodeURIComponent(monitorId)}/hits/${encodeURIComponent(signalId)}/briefing`,
      { timeout: 60000 },
    );
  }

  /** Watched competitors for one agent, with alias-learning state. */
  async getWatched(monitorId) {
    return this.get(`/monitoring/monitors/${encodeURIComponent(monitorId)}/watched`);
  }

  /** Retry a failed sweep ("Try again now" on the failed state). */
  async retrySweep() {
    return this.post('/monitoring/sweep/retry', {});  }

  /** Per-competitor dossier (timeline + filing velocity). */
  async getDossier(entityId) {
    return this.get(`/monitoring/entities/${encodeURIComponent(entityId)}/dossier`, { timeout: 60000 });
  }
}

// Export singleton instance
export const api = new ApiService();

// Export class for testing or custom instances
export default ApiService;
